import React, { useMemo, useState } from "react";
import {
    Users,
    Building2,
    TrendingUp,
    CalendarCheck,
    Umbrella,
    CheckCircle2,
    Clock,
    Stethoscope,
    Wallet,
    Receipt,
    AlertTriangle,
    PlaneTakeoff,
    RefreshCcw,
} from "lucide-react";

import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    LineChart,
    Line,
    AreaChart,
    Area,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

import "../DashboardStyles/DashboardAdmin.css";

// Charte
const COLORS = {
    primary: "#204385",
    secondary: "#ee645f",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#2196f3",
    grey: "#757575",
};

// Palette charts
const CHART_COLORS = [
    "#204385",
    "#ee645f",
    "#4caf50",
    "#ff9800",
    "#2196f3",
    "#9c27b0",
    "#00bcd4",
];

// Mock data (repris de ton code MUI)
const mockData = {
    general: {
        totalEmployees: 487,
        departments: [
            { name: "IT", count: 85 },
            { name: "RH", count: 32 },
            { name: "Finance", count: 45 },
            { name: "Commercial", count: 120 },
            { name: "Marketing", count: 58 },
            { name: "Production", count: 95 },
            { name: "Logistique", count: 52 },
        ],
        employeeStatus: [
            { name: "CDI", value: 365, percentage: 75 },
            { name: "CDD", value: 82, percentage: 17 },
            { name: "Stagiaires", value: 28, percentage: 6 },
            { name: "Alternants", value: 12, percentage: 2 },
        ],
        internalMobility: 8.5,
        absenteeismRate: 4.2,
    },

    leaves: {
        totalRequests: 156,
        approvalRate: 94,
        avgValidationTime: 1.8,
        absencesByType: [
            { name: "Congé annuel", value: 98, color: COLORS.info },
            { name: "Maladie", value: 35, color: COLORS.error },
            { name: "RTT", value: 18, color: COLORS.success },
            { name: "Congé sans solde", value: 3, color: COLORS.warning },
            { name: "Autres", value: 2, color: COLORS.grey },
        ],
        avgRemainingDays: 12.5,
        monthlyTrend: [
            { month: "Jan", demandes: 95 },
            { month: "Fév", demandes: 88 },
            { month: "Mar", demandes: 102 },
            { month: "Avr", demandes: 125 },
            { month: "Mai", demandes: 145 },
            { month: "Juin", demandes: 168 },
            { month: "Juil", demandes: 198 },
            { month: "Aoû", demandes: 215 },
            { month: "Sep", demandes: 156 },
        ],
    },

    expenses: {
        totalAmount: 87450,
        pendingRequests: 23,
        topCategories: [
            { name: "Déplacements", amount: 34200 },
            { name: "Restauration", amount: 28650 },
            { name: "Hébergement", amount: 24600 },
        ],
        exceedanceRate: 12,
        avgProcessingTime: 3.5,
        monthlyExpenses: [
            { month: "Jan", montant: 65000 },
            { month: "Fév", montant: 71000 },
            { month: "Mar", montant: 78000 },
            { month: "Avr", montant: 82000 },
            { month: "Mai", montant: 89000 },
            { month: "Juin", montant: 95000 },
            { month: "Juil", montant: 72000 },
            { month: "Aoû", montant: 68000 },
            { month: "Sep", montant: 87450 },
        ],
    },

    missions: {
        ongoing: 18,
        completed: 47,
        byDestination: [
            { name: "National", value: 42 },
            { name: "International", value: 23 },
        ],
        byType: [
            { type: "Formation", count: 25 },
            { type: "Client", count: 22 },
            { type: "Technique", count: 12 },
            { type: "Commercial", count: 6 },
        ],
        totalCost: 156800,
        validationRate: 87,
    },
};

function StatCard({ title, value, subtitle, icon, color, trend }) {
    const trendIsPlus = typeof trend === "string" && trend.trim().startsWith("+");

    return (
        <div className="daStatCard">
            <div className="daStatCard__row">
                <div className="daStatCard__left">
                    <div className="daStatCard__title">{title}</div>
                    <div className="daStatCard__value" style={{ color: color || COLORS.primary }}>
                        {value}
                    </div>
                    {subtitle ? <div className="daStatCard__sub">{subtitle}</div> : null}

                    {trend ? (
                        <span className={`daTrend ${trendIsPlus ? "is-plus" : "is-minus"}`}>
              {trend}
            </span>
                    ) : null}
                </div>

                <div
                    className="daStatIcon"
                    style={{
                        color: color || COLORS.primary,
                        background: `${color || COLORS.primary}15`,
                        borderColor: `${color || COLORS.primary}25`,
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="daChartCard">
            <div className="daChartCard__title">{title}</div>
            <div className="daChartCard__body">{children}</div>
        </div>
    );
}

function SectionTitle({ icon, children }) {
    return (
        <div className="daSectionTitle">
            <span className="daSectionTitle__icon">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

export default function DashboardAdmin() {
    const [data, setData] = useState(mockData);
    const [loading, setLoading] = useState(false);

    const lastUpdated = useMemo(() => new Date().toLocaleString("fr-FR"), [loading]);

    const handleRefresh = () => {
        setLoading(true);
        setTimeout(() => setLoading(false), 900);
    };

    return (
        <div className="daPage">
            {/* Header */}
            <header className="daHeader">
                <div>
                    {/*<h1>Tableau de bord</h1>*/}
                    {/*<p>Vue synthétique : RH, congés, notes de frais, missions.</p>*/}
                </div>

                {/*<button className="daBtn daBtn--primary" onClick={handleRefresh} type="button">*/}
                {/*    <RefreshCcw size={16} />*/}
                {/*    {loading ? "Actualisation..." : "Rafraîchir"}*/}
                {/*</button>*/}
            </header>

            <main className="daMain">
                {/* SECTION 1 */}
                <SectionTitle icon={<Users size={18} />}>Indicateurs généraux RH</SectionTitle>

                <div className="daGrid daGrid--4">
                    <StatCard
                        title="Employés actifs"
                        value={data.general.totalEmployees}
                        icon={<Users size={20} />}
                        color={COLORS.primary}
                    />
                    <StatCard
                        title="Taux de mobilité interne"
                        value={`${data.general.internalMobility}%`}
                        icon={<TrendingUp size={20} />}
                        color={COLORS.success}
                    />
                    <StatCard
                        title="Taux d'absentéisme"
                        value={`${data.general.absenteeismRate}%`}
                        icon={<Stethoscope size={20} />}
                        color={COLORS.warning}
                    />
                    <StatCard
                        title="Départements"
                        value={data.general.departments.length}
                        icon={<Building2 size={20} />}
                        color={COLORS.info}
                    />
                </div>

                <div className="daGrid daGrid--2">
                    <ChartCard title="Répartition par département">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={data.general.departments}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <RechartsTooltip />
                                <Bar dataKey="count" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Répartition par statut">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={data.general.employeeStatus}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                                    outerRadius={105}
                                    dataKey="value"
                                >
                                    {data.general.employeeStatus.map((_, idx) => (
                                        <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* SECTION 2 */}
                <SectionTitle icon={<Umbrella size={18} />}>Congés & Absences</SectionTitle>

                <div className="daGrid daGrid--4">
                    <StatCard
                        title="Demandes du mois"
                        value={data.leaves.totalRequests}
                        icon={<CalendarCheck size={20} />}
                        color={COLORS.info}
                    />
                    <StatCard
                        title="Taux de validation"
                        value={`${data.leaves.approvalRate}%`}
                        icon={<CheckCircle2 size={20} />}
                        color={COLORS.success}
                    />
                    <StatCard
                        title="Délai moyen validation"
                        value={`${data.leaves.avgValidationTime}j`}
                        icon={<Clock size={20} />}
                        color={COLORS.secondary}
                    />
                    <StatCard
                        title="Solde moyen restant"
                        value={`${data.leaves.avgRemainingDays}j`}
                        icon={<Umbrella size={20} />}
                        color={COLORS.primary}
                    />
                </div>

                <div className="daGrid daGrid--2">
                    <ChartCard title="Répartition des absences par type">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={data.leaves.absencesByType}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={105}
                                    paddingAngle={3}
                                    dataKey="value"
                                    label={({ name, value }) => `${name}: ${value}`}
                                >
                                    {data.leaves.absencesByType.map((e, idx) => (
                                        <Cell key={`cell-a-${idx}`} fill={e.color} />
                                    ))}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Évolution des demandes de congés">
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={data.leaves.monthlyTrend}>
                                <defs>
                                    <linearGradient id="daConges" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.info} stopOpacity={0.8} />
                                        <stop offset="95%" stopColor={COLORS.info} stopOpacity={0.1} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <RechartsTooltip />
                                <Area
                                    type="monotone"
                                    dataKey="demandes"
                                    stroke={COLORS.info}
                                    fillOpacity={1}
                                    fill="url(#daConges)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* SECTION 3 */}
                <SectionTitle icon={<Wallet size={18} />}>Notes de Frais</SectionTitle>

                <div className="daGrid daGrid--4">
                    <StatCard
                        title="Montant total du mois"
                        value={`${(data.expenses.totalAmount / 1000).toFixed(0)}K MAD`}
                        icon={<Wallet size={20} />}
                        color={COLORS.success}
                        trend="+8%"
                    />
                    <StatCard
                        title="Demandes en attente"
                        value={data.expenses.pendingRequests}
                        icon={<AlertTriangle size={20} />}
                        color={COLORS.warning}
                    />
                    <StatCard
                        title="Taux de dépassement"
                        value={`${data.expenses.exceedanceRate}%`}
                        icon={<Receipt size={20} />}
                        color={COLORS.error}
                    />
                    <StatCard
                        title="Délai moyen traitement"
                        value={`${data.expenses.avgProcessingTime}j`}
                        icon={<Clock size={20} />}
                        color={COLORS.info}
                    />
                </div>

                <div className="daGrid daGrid--2">
                    <ChartCard title="Top 3 catégories de dépenses">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={data.expenses.topCategories} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" width={140} />
                                <RechartsTooltip />
                                <Bar dataKey="amount" fill={COLORS.secondary} radius={[0, 8, 8, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Évolution mensuelle des dépenses">
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={data.expenses.monthlyExpenses}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <RechartsTooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="montant"
                                    stroke={COLORS.secondary}
                                    strokeWidth={3}
                                    dot={{ r: 4 }}
                                    name="Montant (MAD)"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* SECTION 4 */}
                <SectionTitle icon={<PlaneTakeoff size={18} />}>Ordres de Mission</SectionTitle>

                <div className="daGrid daGrid--4">
                    <StatCard
                        title="Missions en cours"
                        value={data.missions.ongoing}
                        icon={<PlaneTakeoff size={20} />}
                        color={COLORS.info}
                    />
                    <StatCard
                        title="Missions terminées"
                        value={data.missions.completed}
                        icon={<CheckCircle2 size={20} />}
                        color={COLORS.success}
                    />
                    <StatCard
                        title="Coût total du mois"
                        value={`${(data.missions.totalCost / 1000).toFixed(0)}K MAD`}
                        icon={<Wallet size={20} />}
                        color={COLORS.secondary}
                    />
                    <StatCard
                        title="Taux de validation"
                        value={`${data.missions.validationRate}%`}
                        icon={<CheckCircle2 size={20} />}
                        color={COLORS.success}
                    />
                </div>

                <div className="daGrid daGrid--2">
                    <ChartCard title="Répartition par destination">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={data.missions.byDestination}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, value }) => `${name}: ${value}`}
                                    outerRadius={105}
                                    dataKey="value"
                                >
                                    {data.missions.byDestination.map((_, idx) => (
                                        <Cell key={`cell-d-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Répartition par type de mission">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={data.missions.byType}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="type" />
                                <YAxis />
                                <RechartsTooltip />
                                <Bar dataKey="count" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                <div className="daFooter">
                    <span>Dernière mise à jour : {lastUpdated}</span>
                </div>
            </main>
        </div>
    );
}
